import { Icon, type ButtonProps } from "@blueprintjs/core";
import { useMemo } from "react";

type WallButtonProps = {
  align?: "left" | "right" | "none";
  disabled?: boolean;
  intent?: ButtonProps["intent"];
  icon?: ButtonProps["icon"];
  muted?: boolean;
  strong?: boolean;
  text?: string;
  backgroundColor?: string;
  color?: string;
  onHandleMouse?: (screenX: number, screenY: number) => void;
};

export function WallButton({
  align = "none",
  backgroundColor,
  color,
  disabled = false,
  icon,
  intent = "none",
  muted = false,
  strong = false,
  text,
  onHandleMouse,
}: WallButtonProps) {
  const classRoundedOuter = useMemo<string>(() => {
    if (align === "left") {
      return "rounded-r-3xl";
    }
    if (align === "right") {
      return "rounded-l-3xl";
    }

    return "rounded-3xl";
  }, [align]);
  const classRoundedInner = useMemo<string>(() => {
    if (align === "left") {
      return "rounded-r-2xl";
    }
    if (align === "right") {
      return "rounded-l-2xl";
    }

    return "rounded-2xl";
  }, [align]);
  const classBgColorOuter = useMemo<string>(
    () => getIntentBackgroundColor(intent),
    [intent],
  );
  const classBgColorInner = useMemo<string>(() => {
    if (strong) {
      return getIntentBackgroundColor(intent);
    }

    return "bg-transparent";
  }, [intent, strong]);
  const classMutedOuter = useMemo<string>(
    () => (muted ? "opacity-15" : "opacity-25"),
    [muted],
  );
  const classMutedInner = useMemo<string>(
    () => (muted ? "opacity-50" : ""),
    [muted],
  );
  const classOuter = useMemo<string>(
    () =>
      `absolute top-0 left-0 w-full h-full ${classMutedOuter} ${classRoundedOuter} ${classBgColorOuter}`,
    [classBgColorOuter, classMutedOuter, classRoundedOuter],
  );
  const classInner = useMemo<string>(
    () =>
      `flex relative m-4 p-4 min-h-36 items-center outline-none ${classRoundedInner} ${classBgColorInner} ${classMutedInner}`,
    [classBgColorInner, classMutedInner, classRoundedInner],
  );

  return (
    <div className="relative">
      <div className={classOuter} />
      <button
        className={classInner}
        disabled={disabled}
        style={{
          backgroundColor,
          color,
        }}
        onPointerDown={(e) => onHandleMouse?.(e.screenX, e.screenY)}
      >
        {!!icon && (
          <Icon
            className="text-slate-100 outline-none"
            icon={icon}
            size={72}
            style={{
              backgroundColor,
              color,
            }}
          />
        )}
        {!!text && (
          <div
            className={`inline-block outline-none ${icon ? "ml-8" : ""} text-slate-100 text-7xl`}
            style={{
              backgroundColor,
              color,
            }}
          >
            {text}
          </div>
        )}
      </button>
    </div>
  );
}

function getIntentBackgroundColor(intent: ButtonProps["intent"]) {
  switch (intent) {
    case "primary":
      return "bg-sky-600";
    case "success":
      return "bg-green-600";
    case "danger":
      return "bg-red-600";
    case "warning":
      return "bg-yellow-600";
    case "none":
      return "bg-slate-100";
    default:
      return "bg-slate-100";
  }
}
